import { useEffect, useState } from 'react'
import type { Session } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'
import { withRetry } from '../lib/query'
import { VillageScreen } from './VillageScreen'
import { SyncBar } from '../components/SyncBar'
import { db, localVillages, clearCachesKeepingOutbox, outboxCounts } from '../lib/db'
import { refreshAll, refreshRefOptions, startAutoSync } from '../lib/sync'
import { TeamScreen } from './TeamScreen'
import { t } from '../lib/i18n'
import { LanguageSwitch } from '../components/LanguageSwitch'

type Profile = { role_code: string; full_name: string | null; is_active: boolean }
type Village = {
  village_id: string
  village_code: string
  village_name_en: string | null
  estimated_households: number | null
  registered_active: number
  coverage_pct: number | null
  has_centroid: boolean
  centroid_source: string | null
}

/**
 * The first screen that proves the whole chain: auth → RLS → real gazetteer.
 *
 * It reads v_village_quality_summary rather than the villages table, because
 * that view is what the dashboard will use and it already applies the caller's
 * row level security (security_invoker = true). If this screen shows the right
 * villages for the right user, every later screen is the same pattern again.
 */
export function VillageListScreen({ session }: { session: Session }) {
  const [profile, setProfile] = useState<Profile | null>(null)
  const [villages, setVillages] = useState<Village[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [open, setOpen] = useState<Village | null>(null)
  const [showTeam, setShowTeam] = useState(false)

  useEffect(() => {
    let cancelled = false

    async function load() {
      setLoading(true)
      setError(null)

      // Show whatever the phone already has, immediately. A village list that
      // needs the network to appear is a village list that does not appear.
      const cached = await localVillages()
      if (!cancelled && cached.length > 0) {
        const raw = (await db.meta.get('villages_raw'))?.value as Village[] | undefined
        setVillages(raw ?? [])
        setLoading(false)
      }

      // Both wrapped: on a phone with a drifting clock the first request after
      // sign-in can be refused for being a fraction of a second early.
      const [me, list] = await Promise.all([
        withRetry(() =>
          supabase.from('v_my_profile').select('role_code, full_name, is_active').maybeSingle(),
        ),
        withRetry(() =>
          supabase
            .from('v_village_quality_summary')
            .select(
              'village_id, village_code, village_name_en, estimated_households, registered_active, coverage_pct, has_centroid, centroid_source',
            )
            .order('village_code'),
        ),
      ])

      if (cancelled) return

      if (me.error) setError(me.error.message)
      else setProfile(me.data as Profile | null)

      if (list.error) {
        // Only complain if there is nothing cached to show. Offline with a
        // cached list is the normal working state, not a failure.
        const cached = await localVillages()
        if (cached.length === 0) setError(list.error.message)
      } else {
        setVillages((list.data ?? []) as Village[])
        await db.meta.put({ key: 'villages_raw', value: list.data })
      }

      setLoading(false)
    }

    load()
    startAutoSync()
    refreshAll().catch(() => {})
    refreshRefOptions().catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  async function signOut() {
    // Refuse while work is unsent. Signing out clears the session, and without
    // a session the queue cannot be uploaded — so this button, at the wrong
    // moment, is the one action in the app that can strand a day's fieldwork
    // on a phone nobody thinks to check.
    const { pending, rejected } = await outboxCounts()
    if (pending + rejected > 0) {
      setError(
        `${pending + rejected} record(s) have not reached the server yet. ` +
          'Send them first — signing out now would leave them stuck on this phone.',
      )
      return
    }
    await clearCachesKeepingOutbox()
    await supabase.auth.signOut()
  }

  if (showTeam) {
    return <TeamScreen myUserId={session.user.id} onBack={() => setShowTeam(false)} />
  }

  if (open) {
    return (
      <VillageScreen
        villageId={open.village_id}
        villageName={open.village_name_en ?? open.village_code}
        villageCode={open.village_code}
        estimatedHouseholds={open.estimated_households}
        roleCode={profile?.role_code ?? null}
        onBack={() => setOpen(null)}
      />
    )
  }

  return (
    <div className="screen">
      <div className="appbar">
        <h1>{t('villages.title')}</h1>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <LanguageSwitch compact />
          {/* Only administrators can do anything on the Team screen, so only
              they are shown the door to it. RLS refuses everyone else anyway;
              this just avoids offering a dead end. */}
          {profile?.role_code === 'admin' && (
            <button className="btn-quiet" onClick={() => setShowTeam(true)}>
              {t('common.team')}
            </button>
          )}
          <button className="btn-quiet" onClick={signOut}>
            {t('common.signOut')}
          </button>
        </div>
      </div>

      <SyncBar />

      <div className="content">
        <p className="muted" style={{ marginTop: 0 }}>
          {t('villages.signedInAs')} <strong>{session.user.email}</strong>
          {profile?.role_code && <> · {profile.role_code.replace('_', ' ')}</>}
        </p>

        {/* Migration 003 creates every new login DISABLED. Without this message
            an unactivated user sees an empty list and assumes the app is broken. */}
        {profile && !profile.is_active && (
          <div className="alert alert-warn" role="status">
            {t('villages.notActive')}
          </div>
        )}

        {error && (
          <div className="alert alert-error" role="alert">
            {error}
          </div>
        )}

        {loading && <p className="spinner">{t('villages.loading')}</p>}

        {!loading && villages.length === 0 && !error && (
          <div className="alert alert-warn" role="status">
            {t('villages.none')}
          </div>
        )}

        <ul className="list">
          {villages.map((v) => (
            <li key={v.village_id} onClick={() => setOpen(v)} style={{ cursor: 'pointer' }}>
              <div className="name">{v.village_name_en ?? '(name not recorded)'}</div>
              <div className="code">
                {v.village_code}
                {v.estimated_households != null && <> · {v.estimated_households} {t('villages.expected')}</>}
              </div>
              <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <span className="pill">
                  {v.registered_active} {t('villages.registered')}
                  {v.coverage_pct != null && <> · {v.coverage_pct}%</>}
                </span>
                {!v.has_centroid && <span className="pill">no map centre yet</span>}
                {v.centroid_source === 'provisional_desk' && (
                  <span className="pill">centre is provisional</span>
                )}
              </div>
              <div className="muted" style={{ marginTop: 6 }}>{t('villages.tapToOpen')}</div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}
